/**
 * Theme bridge for Warm Horizon.
 *
 * This app uses THREE related but separate theme layers. Keep them in sync when
 * changing colors (see STYLING.md):
 *
 * 1. `src/global.css` — source of truth for UI styling
 *    CSS variables (--background, --primary, …) consumed by Tailwind / NativeWind.
 *    Use `className="bg-primary"` in components — this is what you want 99% of the time.
 *
 * 2. `THEME` (below) — JavaScript mirror of the palette
 *    Hex strings for runtime APIs that don't understand Tailwind classes:
 *    charts, SVG fills, native modules, `style={{ color: … }}` edge cases.
 *    Do NOT duplicate styling logic here for normal screens — prefer Tailwind.
 *
 * 3. `NAV_THEME` (below) — React Navigation / Expo Router theme object
 *    Colors native navigation chrome only: stack headers, tab bars, drawer, etc.
 *    Wired in `src/app/_layout.tsx` via `<ThemeProvider value={NAV_THEME[scheme]}>`.
 *    Has a smaller API than THEME (React Navigation defines which keys exist).
 *
 * Light/dark switching:
 * - NativeWind `useColorScheme()` toggles CSS variables (via `.dark:root` in global.css).
 * - The same scheme is passed to `ThemeProvider` so navigation chrome matches the UI.
 */

import { DarkTheme, DefaultTheme } from "expo-router";

import { palette } from "@/constants/colors";

/**
 * Full Warm Horizon token set as hex strings, keyed by color scheme.
 *
 * Keys mirror shadcn/RNR naming (background, primary, mutedForeground, …) so
 * the naming stays consistent with `global.css` and Tailwind classes.
 *
 * Usage examples:
 *   import { THEME } from '@/lib/theme';
 *   const { primary } = THEME.light;
 *
 * Prefer Tailwind in JSX. Reach for THEME when you need a raw color value in JS.
 */
export const THEME = {
    light: {
        background: palette.light.background,
        foreground: palette.light.text,
        card: palette.light.surface,
        cardForeground: palette.light.text,
        popover: palette.light.surface,
        popoverForeground: palette.light.text,
        primary: palette.light.primary,
        primaryForeground: "#FFFFFF",
        primaryHover: palette.light.primaryHover,
        primarySoft: palette.light.primarySoft,
        secondary: palette.light.secondary,
        secondaryForeground: "#FFFFFF",
        secondaryHover: palette.light.secondaryHover,
        secondarySoft: palette.light.secondarySoft,
        muted: palette.light.primarySoft,
        mutedForeground: palette.light.textMuted,
        accent: palette.light.primarySoft,
        accentForeground: palette.light.primary,
        destructive: palette.light.error,
        destructiveForeground: "#FFFFFF",
        border: palette.light.border,
        input: palette.light.border,
        ring: palette.light.primary,
        success: palette.light.success,
        warning: palette.light.warning,
        error: palette.light.error,
        info: palette.light.info,
        radius: "12px",
        radiusSheet: "16px",
        radiusPill: "999px",
    },
    dark: {
        background: palette.dark.background,
        foreground: palette.dark.text,
        card: palette.dark.surface,
        cardForeground: palette.dark.text,
        popover: palette.dark.surface,
        popoverForeground: palette.dark.text,
        primary: palette.dark.primary,
        primaryForeground: "#FFFFFF",
        primaryHover: palette.dark.primaryHover,
        primarySoft: palette.dark.primarySoft,
        secondary: palette.dark.secondary,
        secondaryForeground: "#FFFFFF",
        secondaryHover: palette.dark.secondaryHover,
        secondarySoft: palette.dark.secondarySoft,
        muted: palette.dark.primarySoft,
        mutedForeground: palette.dark.textMuted,
        accent: palette.dark.primarySoft,
        accentForeground: palette.dark.primary,
        destructive: palette.dark.error,
        destructiveForeground: "#FFFFFF",
        border: palette.dark.border,
        input: palette.dark.border,
        ring: palette.dark.primary,
        success: palette.dark.success,
        warning: palette.dark.warning,
        error: palette.dark.error,
        info: palette.dark.info,
        radius: "12px",
        radiusSheet: "16px",
        radiusPill: "999px",
    },
} as const;

export type ColorScheme = keyof typeof THEME;

export type ThemeColorKey = keyof (typeof THEME)["light"];

const NON_COLOR_THEME_KEYS = new Set<ThemeColorKey>([
    "radius",
    "radiusSheet",
    "radiusPill",
]);

/** `text-primary-foreground` → `primaryForeground` */
function kebabToCamel(value: string): string {
    return value.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());
}

/** Maps a Tailwind `text-*` utility to a THEME key, when one exists. */
export function themeColorKeyFromTextClass(
    textClass: string,
): ThemeColorKey | undefined {
    if (!textClass.startsWith("text-")) return undefined;

    const token = textClass.slice("text-".length).split("/")[0];
    const key = kebabToCamel(token) as ThemeColorKey;

    if (NON_COLOR_THEME_KEYS.has(key)) return undefined;
    if (key in THEME.light) return key;

    return undefined;
}

/** Resolves the last semantic `text-*` class in a className string to a THEME hex color. */
export function resolveTextClassColor(
    className: string | undefined,
    scheme: ColorScheme,
): string {
    const textClasses =
        className?.split(/\s+/).filter((c) => c.startsWith("text-")) ?? [];

    for (let i = textClasses.length - 1; i >= 0; i--) {
        const key = themeColorKeyFromTextClass(textClasses[i]);
        if (key) return THEME[scheme][key];
    }

    return THEME[scheme].foreground;
}

/**
 * React Navigation theme — maps THEME tokens onto the limited `colors` API
 * that Expo Router / React Navigation understands for navigation UI.
 *
 * | NAV key       | THEME key     | Affects                          |
 * |---------------|---------------|----------------------------------|
 * | background    | background    | Header / tab bar background      |
 * | text          | foreground    | Header title, tab labels         |
 * | primary       | primary       | Active tab, header tint          |
 * | card          | card          | Card-style header background     |
 * | border        | border        | Header / tab bar dividers        |
 * | notification  | destructive   | Badge / notification accent      |
 *
 * Screen content is styled with Tailwind (`className`), not this object.
 * When you enable headers (`headerShown: true`), they pick up these colors automatically.
 */
export const NAV_THEME: Record<ColorScheme, typeof DefaultTheme> = {
    light: {
        ...DefaultTheme,
        colors: {
            ...DefaultTheme.colors,
            background: THEME.light.background,
            border: THEME.light.border,
            card: THEME.light.card,
            notification: THEME.light.destructive,
            primary: THEME.light.primary,
            text: THEME.light.foreground,
        },
    },
    dark: {
        ...DarkTheme,
        colors: {
            ...DarkTheme.colors,
            background: THEME.dark.background,
            border: THEME.dark.border,
            card: THEME.dark.card,
            notification: THEME.dark.destructive,
            primary: THEME.dark.primary,
            text: THEME.dark.foreground,
        },
    },
};
